import { useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useQuery } from 'react-query'
import { ReactReduxContext } from 'react-redux'
import rbacAPI, { AppRoleError } from '../service/rbacAPI'
import { AppRole, Capability, MyAppRoleResponse } from '../types/rbac.types'
import { isAppRole } from '../utils/capabilities'

// Préfixe des clés : invalidateQueries(APP_ROLE_QUERY_KEY) touche tous les
// couples (userId, accountId) en cache.
export const APP_ROLE_QUERY_KEY = ['me', 'app-role']

// Au-delà de cette ancienneté, un rôle connu n'est plus considéré fiable si le
// rafraîchissement échoue : on repasse en fail-closed.
export const APP_ROLE_MAX_STALE_MS = 10 * 60_000

const noopSubscribe = () => () => {}

// userId / accountId de la session courante, lus dans le store Redux d'auth.
// Tolère l'absence de Provider (tests unitaires) : renvoie alors null / null.
export const useAuthScope = (): { userId: string | null; accountId: string | null } => {
  const store = useContext(ReactReduxContext)?.store
  const subscribe = store ? store.subscribe : noopSubscribe
  const authData = () => (store?.getState() as any)?.auth?.authData
  const userId = useSyncExternalStore(subscribe, () => authData()?.userId ?? null)
  const accountId = useSyncExternalStore(subscribe, () => authData()?.accountId ?? null)
  return { userId, accountId }
}

export interface AppRoleState {
  role: AppRole | ''
  capabilities: ReadonlySet<string>
  // true quand un rôle connu est exposé : toujours après un succès, et après un
  // rafraîchissement en échec tant qu'il a moins de APP_ROLE_MAX_STALE_MS.
  isResolved: boolean
  isLoading: boolean
  isError: boolean
}

// Rôle de l'utilisateur sur le compte actif. Monté sous AuthAccess, donc les
// headers d'auth axios sont déjà posés. Fail-closed tant qu'aucune réponse n'a
// abouti (chargement initial, 503 rbac_unavailable sans donnée) : l'UI masque /
// désactive les écritures sans bloquer la lecture. Un échec de rafraîchissement
// (ex. 503 transitoire au retour de focus) conserve le dernier rôle connu tant
// qu'il a moins de APP_ROLE_MAX_STALE_MS.
export const useAppRole = (): AppRoleState => {
  const { userId, accountId } = useAuthScope()
  const query = useQuery<MyAppRoleResponse, AppRoleError>({
    queryKey: [...APP_ROLE_QUERY_KEY, userId, accountId],
    queryFn: rbacAPI.getMyAppRole,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  // L'ancienneté ne compte que si le dernier rafraîchissement a échoué : un
  // rôle obtenu avec succès reste valable tant qu'aucune erreur ne survient,
  // même sans refetch depuis plus de APP_ROLE_MAX_STALE_MS (édition longue
  // sans changement de fenêtre).
  const staleDeadline = query.dataUpdatedAt + APP_ROLE_MAX_STALE_MS
  const isFresh =
    query.data !== undefined &&
    query.dataUpdatedAt > 0 &&
    (!query.isError || Date.now() <= staleDeadline)

  // En erreur, rien ne provoque de rendu à l'échéance : on en force un pour
  // repasser en fail-closed à temps.
  const [, setExpiredTick] = useState(0)
  useEffect(() => {
    if (!query.isError || query.data === undefined) return
    const delay = staleDeadline - Date.now()
    if (delay <= 0) return
    const timer = setTimeout(() => setExpiredTick((n) => n + 1), delay + 1)
    return () => clearTimeout(timer)
  }, [query.isError, query.data, staleDeadline])

  const data = isFresh ? query.data : undefined
  const capabilities = useMemo(() => new Set(data?.capabilities ?? []), [data])
  return {
    role: data && isAppRole(data.appRole) ? data.appRole : '',
    capabilities,
    isResolved: isFresh,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

// Le tableau `capabilities` du serveur fait foi ; le miroir local
// (utils/capabilities.ts) ne sert qu'au typage et aux tests.
export const useCapability = (capability: Capability): boolean => {
  const { capabilities } = useAppRole()
  return capabilities.has(capability)
}

export default useAppRole
